import { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import {
  ChevronDown,
  Globe,
  Heart,
  LayoutDashboard,
  LogOut,
  Menu,
  Settings as SettingsIcon,
  Ticket,
  User as UserIcon,
  X,
} from "lucide-react";
import Logo from "./Logo";
import { useAuth } from "@/store/auth";

// Primary links stay visible; secondary links collapse into a "More" menu.
const PRIMARY = [
  { label: "Home", to: "/", route: true },
  { label: "Destinations", to: "/destinations", route: true },
  { label: "Hotels", to: "/hotels", route: true },
  { label: "Experiences", to: "/experiences", route: true },
  { label: "AI Guide", to: "/ai-guide", route: true },
];
const MORE = [
  { label: "Stories", to: "/#stories" },
  { label: "Smart Pass", to: "/#smart-pass" },
  { label: "About Us", to: "/#about" },
];

interface NavbarProps {
  overlay?: boolean;
}

export default function Navbar({ overlay = false }: NavbarProps) {
  const [scrolled, setScrolled] = useState(!overlay);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [menu, setMenu] = useState<"more" | "profile" | null>(null);
  const { user, status, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    if (!overlay) return;
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, [overlay]);

  const solid = scrolled || mobileOpen;
  const onDark = overlay && !solid;
  const linkClass = `whitespace-nowrap text-sm font-medium transition-colors ${
    onDark || solid ? "text-white/85 hover:text-gold-400" : "text-navy-800 hover:text-iris-600"
  }`;

  const handleLogout = async () => {
    setMenu(null);
    await logout();
    navigate("/");
  };

  return (
    <header
      className={`fixed inset-x-0 top-0 z-50 transition-colors duration-300 ${
        solid ? "bg-navy-900/95 shadow-lg backdrop-blur" : "bg-transparent"
      }`}
    >
      {/* click-away overlay: closes any open dropdown when clicking outside */}
      {menu && <div className="fixed inset-0 z-40" onClick={() => setMenu(null)} />}

      <nav className="container-page relative z-50 flex h-16 items-center justify-between gap-4">
        <Logo variant={onDark || solid ? "light" : "dark"} withTagline={false} />

        <ul className="hidden items-center gap-6 lg:flex xl:gap-8">
          {PRIMARY.map((l) => {
            const active = l.route && location.pathname === l.to;
            const cls = `${linkClass}${active ? " !text-gold-400" : ""}`;
            return (
              <li key={l.label}>
                {l.route ? (
                  <Link to={l.to} className={cls}>{l.label}</Link>
                ) : (
                  <a href={l.to} className={cls}>{l.label}</a>
                )}
              </li>
            );
          })}
          {/* More dropdown */}
          <li className="relative">
            <button
              onClick={() => setMenu(menu === "more" ? null : "more")}
              className={`flex items-center gap-1 ${linkClass}`}
            >
              More
              <ChevronDown size={14} className={`transition-transform ${menu === "more" ? "rotate-180" : ""}`} />
            </button>
            {menu === "more" && (
              <div className="absolute right-0 top-full mt-3 w-44 overflow-hidden rounded-xl border border-slate-100 bg-white py-1 shadow-lift">
                {MORE.map((m) => (
                  <a
                    key={m.label}
                    href={m.to}
                    onClick={() => setMenu(null)}
                    className="block px-4 py-2.5 text-sm text-navy-800 transition hover:bg-cream hover:text-iris-600"
                  >
                    {m.label}
                  </a>
                ))}
              </div>
            )}
          </li>
        </ul>

        <div className="hidden items-center gap-3 lg:flex">
          <button
            className={`flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-xs font-medium transition ${
              onDark || solid
                ? "border-white/25 text-white/90 hover:bg-white/10"
                : "border-slate-200 text-navy-800 hover:bg-slate-50"
            }`}
          >
            <Globe size={14} /> English <ChevronDown size={12} />
          </button>

          {status === "authenticated" && user ? (
            <div className="relative">
              {/* Profile button: avatar + first name */}
              <button
                onClick={() => setMenu(menu === "profile" ? null : "profile")}
                className={`flex items-center gap-2 rounded-full py-1 pl-1 pr-2 text-sm font-medium transition ${
                  onDark || solid ? "text-white hover:bg-white/10" : "text-navy-900 hover:bg-slate-50"
                }`}
              >
                <span className="grid h-8 w-8 place-items-center rounded-full bg-iris-500 text-white">
                  <UserIcon size={16} />
                </span>
                <span className="max-w-[110px] truncate">{user.full_name.split(" ")[0]}</span>
                <ChevronDown size={14} className={`transition-transform ${menu === "profile" ? "rotate-180" : ""}`} />
              </button>
              {menu === "profile" && (
                <div className="absolute right-0 top-full mt-3 w-56 overflow-hidden rounded-xl border border-slate-100 bg-white py-1 shadow-lift">
                  <div className="border-b border-slate-100 px-4 py-2.5">
                    <div className="truncate text-sm font-semibold text-navy-900">{user.full_name}</div>
                    <div className="truncate text-xs text-slate-500">
                      {user.email ?? user.phone ?? "Signed in"}
                    </div>
                  </div>
                  <MenuLink to="/dashboard" icon={<LayoutDashboard size={16} className="text-iris-500" />} onClick={() => setMenu(null)}>
                    My Dashboard
                  </MenuLink>
                  <MenuLink to="/dashboard?tab=profile" icon={<UserIcon size={16} className="text-iris-500" />} onClick={() => setMenu(null)}>
                    My Profile
                  </MenuLink>
                  <MenuLink to="/dashboard?tab=bookings" icon={<Ticket size={16} className="text-iris-500" />} onClick={() => setMenu(null)}>
                    My Bookings
                  </MenuLink>
                  <MenuLink to="/dashboard?tab=wishlist" icon={<Heart size={16} className="text-iris-500" />} onClick={() => setMenu(null)}>
                    Wishlist
                  </MenuLink>
                  <MenuLink to="/dashboard?tab=settings" icon={<SettingsIcon size={16} className="text-iris-500" />} onClick={() => setMenu(null)}>
                    Settings
                  </MenuLink>
                  <button
                    onClick={handleLogout}
                    className="flex w-full items-center gap-2.5 border-t border-slate-100 px-4 py-2.5 text-sm text-red-600 transition hover:bg-red-50"
                  >
                    <LogOut size={16} /> Logout
                  </button>
                </div>
              )}
            </div>
          ) : (
            <Link to="/login" state={{ from: location.pathname }} className="btn-primary">
              Login / Sign Up
            </Link>
          )}
        </div>

        <button
          className={`lg:hidden ${onDark || solid ? "text-white" : "text-navy-900"}`}
          onClick={() => setMobileOpen((v) => !v)}
          aria-label="Toggle menu"
        >
          {mobileOpen ? <X size={24} /> : <Menu size={24} />}
        </button>
      </nav>

      {/* Mobile drawer */}
      {mobileOpen && (
        <div className="border-t border-white/10 bg-navy-900 lg:hidden">
          <ul className="container-page flex flex-col gap-1 py-4">
            {[...PRIMARY, ...MORE.map((m) => ({ ...m, route: false }))].map((l) => {
              const active = "route" in l && l.route && location.pathname === l.to;
              const cls = `block rounded-md px-2 py-2 text-sm hover:bg-white/5 ${
                active ? "font-semibold text-gold-400" : "text-white/85"
              }`;
              return (
                <li key={l.label}>
                  {"route" in l && l.route ? (
                    <Link to={l.to} onClick={() => setMobileOpen(false)} className={cls}>
                      {l.label}
                    </Link>
                  ) : (
                    <a href={l.to} onClick={() => setMobileOpen(false)} className={cls}>
                      {l.label}
                    </a>
                  )}
                </li>
              );
            })}
            {status === "authenticated" && user ? (
              <>
                <li className="mt-2 border-t border-white/10 pt-2" />
                {[
                  { to: "/dashboard", label: "My Dashboard" },
                  { to: "/dashboard?tab=profile", label: "My Profile" },
                  { to: "/dashboard?tab=bookings", label: "My Bookings" },
                  { to: "/dashboard?tab=wishlist", label: "Wishlist" },
                  { to: "/dashboard?tab=settings", label: "Settings" },
                ].map((m) => (
                  <li key={m.label}>
                    <Link
                      to={m.to}
                      onClick={() => setMobileOpen(false)}
                      className="block rounded-md px-2 py-2 text-sm text-white/85 hover:bg-white/5"
                    >
                      {m.label}
                    </Link>
                  </li>
                ))}
                <li>
                  <button
                    onClick={handleLogout}
                    className="block w-full rounded-md px-2 py-2 text-left text-sm text-red-300 hover:bg-white/5"
                  >
                    Logout
                  </button>
                </li>
              </>
            ) : (
              <li className="mt-2">
                <Link to="/login" className="btn-primary w-full" onClick={() => setMobileOpen(false)}>
                  Login / Sign Up
                </Link>
              </li>
            )}
          </ul>
        </div>
      )}
    </header>
  );
}

function MenuLink({
  to,
  icon,
  onClick,
  children,
}: {
  to: string;
  icon: import("react").ReactNode;
  onClick: () => void;
  children: import("react").ReactNode;
}) {
  return (
    <Link
      to={to}
      onClick={onClick}
      className="flex items-center gap-2.5 px-4 py-2.5 text-sm text-navy-800 transition hover:bg-cream"
    >
      {icon} {children}
    </Link>
  );
}